/**
 * Build a single-sided corner cabinet — a 36"-long, 24"-deep cabinet
 * sitting at the corner end of one wall. Used when the perpendicular
 * wall has a sink (or similar) close to the corner: the perpendicular
 * wall covers the remaining gap with a blank panel, and this single
 * cabinet absorbs the corner geometry on this wall.
 *
 * Same physical footprint as a regular lower cabinet (just longer
 * width). Treated as a corner cabinet for clearance purposes
 * (`customFrontClearance: 0`) — the perpendicular blank panel sits in
 * the room-facing front, not a real walking aisle.
 *
 * Convention: `cornerEnd` is which end of `wall` is at the shared
 * corner — 'left' (bottomLeft) or 'right' (bottomRight). `cornerWallId` is
 * the wall on the other side of that corner: the unit stands against BOTH,
 * face to one and end to the other, so it names both — the same shape
 * `buildCornerCabinet` has always written for the L-shaped unit, and the one
 * `countertopExposure` already assumes ("corner units carry two"). Naming one
 * left every consumer that groups by wall reading this unit as absent from
 * the corner's other side. Optional, because a caller that cannot name the
 * other wall should say so by omission rather than guess.
 *
 * `length` (default the standard 36") and `depth` (default the standard 24")
 * are for a cabinet RUN, which may grow the unit to take a remainder too
 * narrow for a cabinet of its own and builds at the depth the scan measured.
 */

const {
  KITCHEN_CABINET_DIMENSIONS,
  SINGLE_SIDED_CORNER_LENGTH,
} = require('./constants');
const { getWallAxes } = require('./wallMath');

function buildSingleSidedCornerCabinet({
  wall,
  cornerEnd,
  floorY,
  cornerWallId = null,
  length = SINGLE_SIDED_CORNER_LENGTH,
  depth = KITCHEN_CABINET_DIMENSIONS.depth,
  // How much of this unit's face the PERPENDICULAR run covers — the corner
  // box. Defaults to this unit's own depth, which is not a coincidence: a
  // corner is always an interior vertex of ONE run, so both legs are built at
  // one depth (see `cabinet-runs/runDepth.js → cornerBoxFor`). It is a named
  // parameter anyway because it means something different — the neighbour's
  // depth, not ours — and it is what decides where the door goes.
  blindDepth = null,
  // Where the corner actually falls on this wall, in metres from `bottomLeft`.
  // Defaults to the wall end named by `cornerEnd`, which is every corner
  // between two real walls. A peninsula arm meets a wall on the arm's OWN line,
  // one back-panel thickness (1") in from the vertex, and that 1" strip is the
  // arm's panel — so a unit seated at the wall end would stand in it, and the
  // reservation (measured from the junction) would sit 1" off the body.
  cornerParam = null,
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;

  const cornerAt =
    typeof cornerParam === 'number'
      ? cornerParam
      : cornerEnd === 'left'
        ? 0
        : axes.wallLen;
  const segmentStart = cornerEnd === 'left' ? cornerAt : cornerAt - length;
  const segmentEnd = segmentStart + length;
  const segmentMid = (segmentStart + segmentEnd) / 2;

  const surfacePoint = {
    x: axes.start.x + segmentMid * axes.wallDir.x,
    z: axes.start.z + segmentMid * axes.wallDir.z,
  };

  const cabinetDepth = depth;
  const cabinetHeight = KITCHEN_CABINET_DIMENSIONS.height;

  const center = {
    x: surfacePoint.x + (cabinetDepth / 2) * axes.wallNormal.x,
    y: floorY + cabinetHeight / 2,
    z: surfacePoint.z + (cabinetDepth / 2) * axes.wallNormal.z,
  };

  const stamp = `${wall.id}_${cornerEnd}`;

  return {
    id: `single_sided_corner_cabinet_${stamp}`,
    type: 'corner-cabinet',
    label: 'Single-sided Corner Cabinet',
    category: { 'corner-cabinet': true, 'lower-cabinet': true },
    center,
    dimensions: {
      x: length,
      y: cabinetHeight,
      z: cabinetDepth,
      // Marker so the renderer / inspector can tell the two corner-
      // cabinet variants apart. Distinct from the L-shape's `alongA` /
      // `alongB` / `cornerBox` metadata.
      cornerVariant: 'single-sided',
      // The blind half, and which end of the unit it is on, so the renderer
      // can put ONE door on the portion you can actually reach. Without these
      // it draws two doors across the whole face and buries a handle inside
      // the perpendicular cabinet.
      blindDepth: blindDepth ?? cabinetDepth,
      blindEnd: cornerEnd,
    },
    rotation: { x: 0, y: ry, z: 0 },
    // Own wall FIRST: every `parentWallIds[0]` reader downstream means the
    // wall this unit's face is on, and there are many.
    parentWallIds:
      cornerWallId && cornerWallId !== wall.id
        ? [wall.id, cornerWallId]
        : [wall.id],
    fixtureGroupId: `kitchenSingleSidedCornerCabinet_${stamp}`,
    customFrontClearance: 0,
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
    cornerEnd,
    range: { start: segmentStart, end: segmentEnd },
  };
}

module.exports = { buildSingleSidedCornerCabinet };
